// src/hooks/usePageMeta.ts
// Sets the document title, description, canonical URL and social tags for a page, then restores
// the previous values on unmount. The Cloudflare worker serves the same values to crawlers that
// do not run JavaScript.
import { useEffect } from 'react';

export const SITE_ORIGIN = 'https://www.chots.ai';
const JSON_LD_ID = 'page-json-ld';

export interface PageMeta {
  title: string;
  description: string;
  /** Path such as /blog/my-post/. */
  path: string;
  image?: string;
  ogType?: 'website' | 'article';
  jsonLd?: Record<string, unknown>;
}

const upsertMeta = (attr: 'name' | 'property', key: string, content: string): (() => void) => {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  const previous = element?.getAttribute('content') ?? null;
  const created = !element;
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
  return () => {
    if (created) element?.remove();
    else if (previous !== null) element?.setAttribute('content', previous);
  };
};

const upsertCanonical = (href: string): (() => void) => {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  const previous = link?.getAttribute('href') ?? null;
  const created = !link;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
  return () => {
    if (created) link?.remove();
    else if (previous !== null) link?.setAttribute('href', previous);
  };
};

const usePageMeta = ({ title, description, path, image, ogType = 'website', jsonLd }: PageMeta): void => {
  const jsonLdText = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    const url = `${SITE_ORIGIN}${path}`;
    const previousTitle = document.title;
    document.title = title;

    const restorers = [
      upsertMeta('name', 'description', description),
      upsertMeta('property', 'og:title', title),
      upsertMeta('property', 'og:description', description),
      upsertMeta('property', 'og:url', url),
      upsertMeta('property', 'og:type', ogType),
      upsertMeta('name', 'twitter:title', title),
      upsertMeta('name', 'twitter:description', description),
      upsertCanonical(url),
    ];
    if (image) {
      restorers.push(upsertMeta('property', 'og:image', image), upsertMeta('name', 'twitter:image', image));
    }

    let script: HTMLScriptElement | null = null;
    if (jsonLdText) {
      script = document.createElement('script');
      script.id = JSON_LD_ID;
      script.type = 'application/ld+json';
      script.text = jsonLdText;
      document.head.appendChild(script);
    }

    return () => {
      document.title = previousTitle;
      restorers.forEach((restore) => restore());
      script?.remove();
    };
  }, [title, description, path, image, ogType, jsonLdText]);
};

export default usePageMeta;
